import { z } from 'zod/v3';

const path = z.string().max(300);
export const CustomRestMappingSchema = z.object({
  uniqueId: path.min(1),
  fullName: path.optional(),
  firstName: path.optional(),
  lastName: path.optional(),
  email: path.optional(),
  username: path.optional(),
  mfaEnabled: path.optional(),
  role: path.optional(),
}).refine((mapping) => Boolean(mapping.email || mapping.username), 'Map email or username');

export const CustomRestRecipeSchema = z.object({
  auth: z.object({
    headerName: z.string().regex(/^[!#$%&'*+.^_`|~0-9A-Za-z-]+$/).max(100).default('Authorization')
      .refine((name) => !['host', 'cookie', 'content-length', 'connection', 'transfer-encoding'].includes(name.toLowerCase()), 'This header cannot carry credentials'),
    headerFormat: z.string().max(500).default('Bearer {{apiKey}}')
      .refine((format) => format.includes('{{apiKey}}') && !/[\r\n]/.test(format), 'Include {{apiKey}} without line breaks'),
  }),
  request: z.object({
    url: z.string().url().max(2000),
    queryParams: z.record(z.string().max(1000)).optional(),
  }),
  pagination: z.discriminatedUnion('strategy', [
    z.object({ strategy: z.literal('none') }),
    z.object({ strategy: z.literal('cursor'), cursorPath: path.min(1), cursorParam: z.string().min(1).max(100) }),
    z.object({ strategy: z.literal('link'), nextUrlPath: path.min(1) }),
    z.object({ strategy: z.literal('offset'), offsetParam: z.string().min(1).default('offset'), limitParam: z.string().min(1).default('limit'), limit: z.number().int().min(1).max(500).default(100) }),
  ]),
  itemsPath: path,
  scheduleDays: z.number().int().min(1).max(365).optional(),
  /** Omitted on the original access-review account recipe. */
  dataset: z.object({ resourceType: z.string().regex(/^[a-z][a-z0-9_]{0,63}$/), idPath: path.min(1), displayNamePath: path.optional() }).optional(),
  mapping: CustomRestMappingSchema,
});
export type CustomRestRecipe = z.infer<typeof CustomRestRecipeSchema>;
export type CustomRestMapping = z.infer<typeof CustomRestMappingSchema>;

export interface CustomRestMappedUser {
  externalId: string;
  email: string | null;
  username: string | null;
  displayName: string | null;
  isMfaEnrolled: boolean | null;
  role: string | null;
}

export const CustomDatasetConfigSchema = z.object({
  idPath: path.min(1).default('id'),
  displayNamePath: path.default('name'),
  schema: z.record(z.unknown()),
  canonicalType: z.string().optional(),
  canonicalMapping: z.record(path).default({}),
});
export type CustomDatasetConfig = z.infer<typeof CustomDatasetConfigSchema>;

export const CustomDataConditionSchema = z.object({
  path: path.min(1),
  operator: z.enum(['equals', 'not_equals', 'gt', 'gte', 'lt', 'lte', 'contains', 'in', 'exists', 'not_exists']),
  value: z.unknown().optional(),
  quantifier: z.enum(['scalar', 'any', 'all']).default('scalar'),
}).superRefine((condition, ctx) => {
  if (!['exists', 'not_exists'].includes(condition.operator) && condition.value === undefined) ctx.addIssue({ code: 'custom', message: 'A comparison value is required', path: ['value'] });
  if (['gt', 'gte', 'lt', 'lte'].includes(condition.operator) && typeof condition.value !== 'number') ctx.addIssue({ code: 'custom', message: 'Numeric comparisons require a JSON number', path: ['value'] });
  if (condition.operator === 'in' && !Array.isArray(condition.value)) ctx.addIssue({ code: 'custom', message: 'In requires a JSON array', path: ['value'] });
});
export const CustomDataTestConfigSchema = z.object({
  description: z.string().max(2000).default(''),
  filters: z.array(CustomDataConditionSchema).max(20).default([]),
  groups: z.array(z.object({
    match: z.enum(['all', 'any']).default('all'),
    conditions: z.array(CustomDataConditionSchema).min(1).max(20),
  })).min(1).max(10),
  match: z.enum(['all', 'any', 'exactly_one']).default('all'),
  exclusions: z.array(z.object({ externalId: z.string().min(1).max(300), reason: z.string().trim().min(1).max(500) })).max(500).default([]),
  maxAgeHours: z.number().int().min(1).max(8760).default(48),
  frequencyDays: z.number().int().min(1).max(365).default(1),
  frameworkRequirements: z.array(z.object({ frameworkDefId: z.string().min(1), requirementRef: z.string().min(1) })).max(50).default([]),
});
export type CustomDataTestConfig = z.infer<typeof CustomDataTestConfigSchema>;
export type CustomDataCondition = z.infer<typeof CustomDataConditionSchema>;
